import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Typography } from '@mui/material';
import { PublicProduct, publicStorefrontApi } from '../../services/storefrontService';
import { useStorefront } from '../../components/Storefront/StorefrontContext';
import StorefrontProductCard from '../../components/Storefront/StorefrontProductCard';
import { useStorefrontT } from '../../components/Storefront/useStorefrontT';
import MaterialSymbol from '../../components/common/MaterialSymbol';
import { MATERIAL_SYMBOLS_SUBSET } from '../../designSystemIcons';

const iconName = (icon: string) => (MATERIAL_SYMBOLS_SUBSET.includes(icon) ? icon : 'check_circle');

/** `/store/:slug` — Hero, Vorteile, empfohlene Kurse, Stimmen, FAQ (Plan 3.8). */
const StorefrontHome: React.FC = () => {
    const { t } = useStorefrontT();
    const { slug, storefront } = useStorefront();
    const [featured, setFeatured] = useState<PublicProduct[]>([]);

    useEffect(() => {
        let cancelled = false;
        publicStorefrontApi.get(slug).then((d) => !cancelled && setFeatured(d.featured)).catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [slug]);

    return (
        <Box>
            <Grid container spacing={4} alignItems="center" sx={{ mb: 6 }}>
                <Grid size={{ xs: 12, md: storefront.heroImageUrl ? 7 : 12 }}>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                        {storefront.heroTitle || storefront.displayName}
                    </Typography>
                    {storefront.heroSubtitle ? <Typography variant="h6" color="text.secondary" gutterBottom>{storefront.heroSubtitle}</Typography> : null}
                    {storefront.heroDescription ? <Typography sx={{ mb: 3, whiteSpace: 'pre-line' }}>{storefront.heroDescription}</Typography> : null}
                    <Button variant="contained" size="large" component={RouterLink} to={`/store/${slug}/catalog`}>{t('hero.cta')}</Button>
                </Grid>
                {storefront.heroImageUrl ? (
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Box component="img" src={storefront.heroImageUrl} alt="" sx={{ width: '100%', borderRadius: 2, objectFit: 'cover', maxHeight: 360 }} />
                    </Grid>
                ) : null}
            </Grid>

            {featured.length > 0 ? (
                <Box sx={{ mb: 6 }} data-testid="storefront-featured">
                    <Typography variant="h5" component="h2" gutterBottom>{t('featured')}</Typography>
                    <Grid container spacing={3}>
                        {featured.map((p) => (
                            <Grid key={p.id} size={{ xs: 12, sm: 6, md: 4 }}><StorefrontProductCard product={p} /></Grid>
                        ))}
                    </Grid>
                </Box>
            ) : null}

            {storefront.features.length > 0 ? (
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" component="h2" gutterBottom>{t('features')}</Typography>
                    <Grid container spacing={3}>
                        {storefront.features.map((f, i) => (
                            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: 'var(--oa-paper, #f4f4f4)', color: 'var(--oa-text-2, #677e8b)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <MaterialSymbol name={iconName(f.icon)} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: 600 }}>{f.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{f.description}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ) : null}

            {storefront.testimonials.length > 0 ? (
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" component="h2" gutterBottom>{t('testimonials')}</Typography>
                    <Grid container spacing={3}>
                        {storefront.testimonials.map((q, i) => (
                            <Grid key={i} size={{ xs: 12, md: 4 }}>
                                <Box component="figure" sx={{ m: 0, p: 2, border: '1px solid var(--oa-divider, #dee2e6)', borderRadius: 1 }}>
                                    <Typography component="blockquote" sx={{ m: 0, mb: 1 }}>{q.quote}</Typography>
                                    <Typography component="figcaption" variant="body2" color="text.secondary">{q.author}{q.role ? `, ${q.role}` : ''}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ) : null}

            {storefront.faq.length > 0 ? (
                <Box>
                    <Typography variant="h5" component="h2" gutterBottom>{t('faq')}</Typography>
                    {storefront.faq.map((item, i) => (
                        <Accordion key={i} disableGutters variant="outlined">
                            <AccordionSummary expandIcon={<MaterialSymbol name="expand_more" />}>
                                <Typography sx={{ fontWeight: 600 }}>{item.question}</Typography>
                            </AccordionSummary>
                            <AccordionDetails><Typography sx={{ whiteSpace: 'pre-line' }}>{item.answer}</Typography></AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            ) : null}
        </Box>
    );
};

export default StorefrontHome;
